import React, { useEffect, useRef, useState, useImperativeHandle } from 'react';

import './index.module.less';

import { debounce } from 'lodash';
import { Api } from '@apis/client';
import Http from '@helpers/Http';
import { defaultPoint } from '@helpers/Constants';
import FridayService from '@services/friday';
import { ShapeType } from '../constants';
import { transformToGcj02, transformToBd09 } from '@utils/mapUtil';

import BaseMap from '@components/map/Map/BaseMap';
import { showLoading, hideLoading } from '@widgets/Loading';
import Toast from '@widgets/Toast';
import Slider from './Slider';
import List from './List';
import SearchList from '../widgets/SearchListModal';
import { RegionData } from '@apis/gloabal';

const minRadius = 100; //最小半径
const valueArr = [100, 200, 300, 500, 1000];
const maxZoom = 19;
const minZoom = 1;

interface IProps {
  editData?: RegionData;
  centerPoint?: { lng: number; lat: number };
  urlParams: { [key: string]: string };
}

const CircleDetail = React.forwardRef(
  (props: IProps, ref): JSX.Element => {
    const { editData, centerPoint, urlParams } = props;
    const mapRef = useRef(null);
    const [center, setMapCenter] = useState(centerPoint || defaultPoint);
    const [locations, setLocations] = useState(null);
    const [searchLocations, setSearchLocations] = useState(null);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [isSearching, setSearchStatus] = useState(false); //是否展开列表
    const [radius, setRadius] = useState(minRadius);
    const timer = useRef(null);
    const [sliderValue, setSliderValue] = useState(0);

    const inputValue = (value: string) => {
      search(value);
    };

    //根据输入地点模糊查找位置信息
    const search = debounce(async (value?: string) => {
      if (!value) {
        setSearchLocations([]);
        hideLoading();
        return;
      }
      clearTimeout(timer.current);
      if (!navigator.onLine) {
        return Toast.show({
          type: 'strip',
          title: '网络异常，请重试',
        });
      }
      showLoading();
      //搜索超时限制6s
      timer.current = setTimeout(() => {
        hideLoading();
        Toast.show({
          type: 'strip',
          title: '网络异常，请重试',
        });
      }, 6000);
      const instanceMap = mapRef.current;
      const result = await instanceMap.locationSearch(value, center);
      hideLoading();
      clearTimeout(timer.current);
      setSearchLocations(result || []);
    }, 500);

    //根据当前地址查找附近位置
    const getPois = async (centers?: any, r?: number) => {
      const centerData = centers || center;
      const instanceMap = mapRef.current;
      const list = await instanceMap.getLocationPois(centerData, r || radius);
      setLocations(list);
      setSelectedLocation(list[0]);
    };

    const onSelectItem = (item: any, isSearch?: boolean) => {
      FridayService.addEvent(
        'security_guard_add_edit_guard_area_regional_location_select_address_click',
      );
      setSearchStatus(false);
      setSelectedLocation(item);
      const instanceMap = mapRef.current;
      if (isSearch) {
        setSearchLocations(null);
        const c = {
          lng: item.lng || item.point.lng,
          lat: item.lat || item.point.lat,
        };
        instanceMap.setCenter(c);
        getPois(c);
        setMapCenter(c);
      }
      // const centerData = item.lng
      //   ? { lng: item.lng, lat: item.lat }
      //   : item.point;
      // instanceMap.centerAndZoom(
      //   new BMap.Point(centerData.lng, centerData.lat),
      //   instanceMap.getZoom(),
      // );
    };

    const onMapLoad = () => {
     console.log('map load');
     initMapEvent();
    };

    const initMapEvent = () => {
      if (editData) {
        setEditPosition();
      } else {
        const instanceMap = mapRef.current;
        const tempLocation = centerPoint;
        instanceMap?.setCenter(tempLocation);
        setMapCenter(tempLocation);
        getPois(tempLocation);
      }
      const instanceMap = mapRef.current;
      instanceMap.addListener('dragend', getTargeDetail);
    }

    useEffect(() => {
      setTimeout(() => {
        initMapEvent();
      }, 1500)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    //获取移动后的中心点坐标
    const getTargeDetail = (e: any) => {
      FridayService.addEvent(
        'security_guard_add_edit_guard_area_regional_location_drag_the_map',
      );
      const instanceMap = mapRef.current;
      setSelectedLocation(null);
      const p = instanceMap.getCenter();
      const targetPoint = p;
      setMapCenter(targetPoint);
      getPois(targetPoint);
    };

    //设置定位参数（如果是编辑定位地点时）
    const setEditPosition = () => {
      const instanceMap = mapRef.current;
      const editRadius = Math.floor(editData.radius);
      const idx = valueArr.findIndex(item => item === editRadius);
      const l = 18 - idx;
      const tempPoint = editData.points[0];
      const location = {
        lng: tempPoint[0],
        lat: tempPoint[1],
      };

      instanceMap.setZoomAndCenter(location, l)
      setMapCenter(location);
      setRadius(editRadius);
      setSliderValue(idx);
      getPois(location);
    };

    //获取设备定位信息
    const getPosition = async () => {
      FridayService.addEvent(
        'security_guard_add_edit_guard_area_regional_location_locate_the_device_location_click',
      );
      setSelectedLocation(null);
      const { data } = await Http.post(Api.GetCardInfoBySncode, {
        snCode: urlParams.sncode,
        company: urlParams.company || 'lingge',
      });

      if (!data?.isOnLine) {
        Toast.show({
          type: 'strip',
          title: '设备离线，请联网后设置',
        });
      }

      //设备在线时下发实时定位指令
      if (data?.isOnLine) {
        await Http.post(Api.PostPositionV1Realtime, {
          snCode: urlParams.sncode,
        }).catch(e => {});
      }
      //获取定位信息(如果设备有历史定位，则使用历史定位，没有历史定位时使用默认位置为北京市定位
      const { data: result } = await Http.post(Api.GetPositionV1Realtime, {
        snCode: urlParams.sncode,
      });
      const instanceMap = mapRef.current;

      let location = defaultPoint;
      if (result) {
        location = transformToBd09({ lng: result.longitude, lat: result.latitude });
      }
      instanceMap.setCenter(location);
      getPois(location);
      setMapCenter(location);
    };

    const cancelSearch = () => {
      setSearchStatus(false);
      setSearchLocations([]);
    };

    const controlSearchList = () => {
      FridayService.addEvent(
        'security_guard_add_edit_guard_area_regional_location_search_click',
      );
      setSearchStatus(true);
    };

    const saveData = () => {
      const currentPoint = transformToGcj02({ lng: selectedLocation.lng || selectedLocation.point.lng, lat: selectedLocation.lat || selectedLocation.point.lat });
      const tempPoint = [
        currentPoint.lng,
        currentPoint.lat,
      ];
      console.log(tempPoint, 'tempPoint');
      console.log( selectedLocation.lng , 'lng');
      console.log( selectedLocation.lat , 'lat');
      const condition = {
        shape: ShapeType.circle,
        address: selectedLocation.address || selectedLocation.title,
        points: [tempPoint],
        radius: parseFloat((radius + 0.01).toFixed(2)),
      };
      return condition;
    };
    const onChangeSlider = (value: any) => {
      FridayService.addEvent(
        'security_guard_add_modify_guard_area_guard_range_drag_click',
      );
      const instanceMap = mapRef.current;
      if (value > sliderValue) {
        const targetZoom = instanceMap.getZoom() - (value - sliderValue);
        instanceMap.setZoom(targetZoom);
      } else {
        const targetZoom = instanceMap.getZoom() + (sliderValue - value);
        instanceMap.setZoom(targetZoom);
      }
      const targetRadius = valueArr[value];
      setSliderValue(value);
      setRadius(targetRadius);
      getPois(null, targetRadius);
    };

    useImperativeHandle(ref, () => ({
      save: saveData,
      reset: () => {},
      getMapCenter: () => center,
    }));

    useEffect(() => {
      return () => {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        const instanceMap = mapRef.current;
        if (instanceMap) {
          instanceMap?.removeListener('dragend', getTargeDetail);
        }
      };
      // eslint-disable-next-line
    }, []);

    useEffect(() => {
      // 记录窗口高度，避免 安卓手机 键盘挤小屏幕导致页面变形
      document.body.style.overflow = 'hidden';
      const originalHeight =
        document.documentElement.clientHeight || document.body.clientHeight;
      window.onresize = function() {
        document.body.style.height = originalHeight + 'px';
      };

      return () => {
        window.onresize = null;
        document.body.style.overflow = 'auto';
      };
    }, []);

    const radiusText =
      radius >= 1000 ? (radius / 1000).toFixed(0) + 'km' : radius + 'm';
    const mapContainerHeight = document.body.clientHeight * 0.77;
    return (
      <div style={{ width: '100%', height: mapContainerHeight }}>
        <BaseMap
          ref={mapRef}
          onMapLoad={onMapLoad}
          center={center}
          maxZoom={maxZoom}
          minZoom={minZoom}
          zoom={18}
          enablePinchToZoom={false}
          enableDoubleClickZoom={false}
        >
        </BaseMap>
        <div
          className="flex flex-center"
          styleName="icon-genlocation"
          onClick={getPosition}
          style={{
            position: 'absolute',
            right: 19,
            bottom:  mapContainerHeight * 0.55
          }}
        >
          <i className="iconfont icon-dingwei" />
          </div>
        <div className="flex" styleName="slider-wrapper"
          style={{
            position: 'absolute',
            left: 19,
            bottom: mapContainerHeight * 0.44,
            width: document.body.clientWidth - 19 * 2
          }}
          >
            <div>守护范围</div>
            <div styleName="slider">
              <Slider
                max={valueArr.length - 1}
                min={0}
                step={1}
                value={sliderValue}
                markStep={1}
                onChange={onChangeSlider}
                showMarks={true}
              />
            </div>
          </div>
        <div
          className="flex flex-center"
          styleName="map-circle"
          style={{
            position: 'absolute',
            left: (document.body.clientWidth - document.body.clientWidth * 0.73) / 2,
            top: (mapContainerHeight - document.body.clientWidth * 0.8) / 2,
            width: document.body.clientWidth * 0.73,
            height: document.body.clientWidth * 0.73,
          }}
        >
          <div styleName="map-marker"></div>
          <div styleName="map-line">
            <div styleName="map-line-text">{radiusText}</div>
          </div>
        </div>
        <List
          items={locations}
          onShowSearch={controlSearchList}
          onSelectItem={onSelectItem}
          selectedLocation={selectedLocation}
        />
        {isSearching ? (
          <SearchList
            items={searchLocations}
            onSelectItem={onSelectItem}
            selectedLocation={selectedLocation}
            onCancel={cancelSearch}
            onSearch={inputValue}
          />
        ) : null}
      </div>
    );
  },
);
export default CircleDetail;
